import { makeAutoObservable, toJS, runInAction } from 'mobx';
import type { PopulatedMenu, PopulatedSection } from 'root/types/menusTypes';
import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { MenuAvailability } from '@wix/restaurants-olo-operations-client-commons';
import {
  extractScheduledAvailabilityData,
  getMenuAvailabilityStatusText,
} from 'root/utils/menusUtils';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type {
  ItemModalAvailabilityStatusKeys,
  MenuAvailabilityStatusKeys,
} from 'root/availabilityStatusKeys';

export class MenuState {
  menu: PopulatedMenu;
  sectionSizes: Record<string, number> = {};
  private availabilityStatus?: AvailabilityStatus;
  private menuAvailability?: MenuAvailability;
  private nextAvailableTimeslot?: boolean;

  constructor(menu: PopulatedMenu) {
    this.menu = menu;
    this.sectionSizes = menu.sections.reduce((acc, section) => {
      acc[section.id] = section.itemIds?.length ?? 0;
      return acc;
    }, {} as Record<string, number>);
    makeAutoObservable(this);
  }

  get id() {
    return this.menu.id;
  }

  get size() {
    return Object.values(this.sectionSizes).reduce((acc, size) => acc + size, 0);
  }

  get sectionMap() {
    return this.menu.sections.reduce((acc, section) => {
      acc.set(section.id, section);
      return acc;
    }, new Map<string, PopulatedSection>());
  }

  setSize(size: number) {
    if (this.size !== size) {
      let itemsLeft = size;
      this.sectionSizes = this.menu.sections.reduce((acc, section) => {
        acc[section.id] = Math.min(section.itemIds?.length ?? 0, itemsLeft);
        itemsLeft -= (section.itemIds || []).length;
        itemsLeft = Math.max(itemsLeft, 0);
        return acc;
      }, {} as Record<string, number>);
    }
  }

  get isAvailable() {
    return this.availabilityStatus !== AvailabilityStatus.UNAVAILABLE;
  }

  get availability() {
    return this.menuAvailability;
  }

  get hasNextAvailableTimeslot() {
    return this.nextAvailableTimeslot;
  }

  get menuDto() {
    const menu = toJS(this.menu);
    const sections = this.menu.sections.map((section) => {
      const sectionSize = this.sectionSizes[section.id];
      return {
        ...section,
        size: sectionSize,
        truncated: (section.itemIds?.length ?? 0) > sectionSize,
      };
    });
    return {
      _id: this.menu.id,
      ...menu,
      sections,
    };
  }

  updateAvailabilityStatus(availabilityStatus: AvailabilityStatus) {
    runInAction(() => {
      this.availabilityStatus = availabilityStatus;
    });
  }

  updateMenuAvailability(menuAvailability: MenuAvailability) {
    runInAction(() => {
      this.menuAvailability = menuAvailability;
    });
  }

  updateNextAvailableTimeslot(hasNextAvailableTimeslot: boolean) {
    runInAction(() => {
      this.nextAvailableTimeslot = hasNextAvailableTimeslot;
    });
  }

  getScheduledAvailabilityData() {
    return extractScheduledAvailabilityData(this.availability);
  }

  getAvailabilityStatus({
    locale,
    timezone,
    t,
    keys,
  }: {
    locale: string;
    timezone: string;
    t: TFunction;
    keys: MenuAvailabilityStatusKeys | ItemModalAvailabilityStatusKeys;
  }) {
    const hasNextAvailability = !!this.hasNextAvailableTimeslot;
    return !this.isAvailable && this.availability
      ? getMenuAvailabilityStatusText({
          availability: this.availability,
          locale,
          timezone,
          t,
          keys: hasNextAvailability ? keys.hasNextAvailabilityKeys : keys.noNextAvailabilityKeys,
        })
      : { shouldCollapseAvailabilityStatus: true, text: '' };
  }
}
